/**
 * 換算設定の画面で行うバリデーション（プロダクト仕様書「ブラウザでのバリデーション」）。
 * サーバーのチェックのうち、適用開始日の形と当日より後、数値の形式、軸の重複、行が1つ以上、下限が0の段階があるかを行う。
 */

import { messages } from './messages';
import { parseNumberInput } from './number';
import type { TableDef } from './tables';

const DATE_SHAPE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `YYYY-MM-DD` の実在する日付か */
function isDate(input: string): boolean {
  const match = DATE_SHAPE.exec(input);
  if (!match) return false;
  const [y, m, d] = match.slice(1).map((s) => parseInt(s, 10));
  const date = new Date(0);
  // Date.UTC は0〜99年を1900年代として扱うため setUTCFullYear を使う
  date.setUTCFullYear(y, m - 1, d);
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}

/** 適用開始日の単項目チェック。当日はサーバーが求めた日付（`YYYY-MM-DD`）で、ブラウザの時計は使わない */
export function checkDate(input: string, today: string): string | null {
  if (input === '') return messages.dateBlank;
  if (!isDate(input)) return messages.dateFormat;
  // 同じ形の日付どうしは文字列の大小で比べられる
  if (input <= today) return messages.dateNotAfterToday(today);
  return null;
}

/** 当日の翌日（適用開始日の入力欄の最小値） */
export function nextDay(today: string): string {
  const [y, m, d] = today.split('-').map((s) => parseInt(s, 10));
  const date = new Date(0);
  date.setUTCFullYear(y, m - 1, d + 1);
  return [
    String(date.getUTCFullYear()).padStart(4, '0'),
    String(date.getUTCMonth() + 1).padStart(2, '0'),
    String(date.getUTCDate()).padStart(2, '0'),
  ].join('-');
}

/** 数値の入力欄の単項目チェック（空欄を含む数値の形式） */
export function checkNumber(input: string): string | null {
  const result = parseNumberInput(input);
  if (result.ok) return null;
  return result.blank ? messages.numberBlank : messages.numberFormat;
}

export interface RowInput {
  key: string;
  values: Record<string, string>;
}

/** 軸の値を DB に書き込む値で比べるための形。数値の形式でなければ null（重複を判定しない） */
export function axisValue(table: TableDef, row: RowInput): string | null {
  const axis = table.hasAxis ? table.columns[0] : undefined;
  if (!axis) return null;
  const input = row.values[axis.name] ?? '';
  if (axis.kind === 'consumption_item') return input;
  const result = parseNumberInput(input);
  return result.ok ? result.canonical : null;
}

/** 軸の値がほかの行と重複する行 */
export function duplicatedRows(table: TableDef, rows: RowInput[]): Set<string> {
  const keysByAxis = new Map<string, string[]>();
  for (const row of rows) {
    const value = axisValue(table, row);
    if (value === null) continue;
    keysByAxis.set(value, [...(keysByAxis.get(value) ?? []), row.key]);
  }
  return new Set([...keysByAxis.values()].filter((keys) => keys.length > 1).flat());
}

export interface RowsCheckResult {
  /** 行ごとのエラー（軸の重複） */
  rowErrors: Record<string, string>;
  /** 行にも入力欄にも属さないエラー */
  tableErrors: string[];
}

/** 行とテーブル全体のチェック。単項目チェックでエラーがないときだけ行う */
export function checkRows(table: TableDef, rows: RowInput[]): RowsCheckResult {
  const tableErrors: string[] = [];
  if (rows.length === 0) tableErrors.push(messages.noRows);

  const rowErrors: Record<string, string> = {};
  const duplicateMessage =
    table.columns[0]?.kind === 'consumption_item'
      ? messages.duplicateConsumptionItem
      : messages.duplicateLowerBound;
  for (const key of duplicatedRows(table, rows)) {
    rowErrors[key] = duplicateMessage;
  }

  if (table.name === 'store_sku_progressive_rates') {
    const hasZeroStep = rows.some((row) => axisValue(table, row) === '0');
    if (!hasZeroStep) tableErrors.push(messages.noZeroStep);
  }
  return { rowErrors, tableErrors };
}
